import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Select,
    MenuItem,
    FormControl,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
} from '@material-ui/core';
import { CheckCircleOutline, AddCircleOutline, CloudUpload, Edit } from '@material-ui/icons';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
    mediateEntriesInfoDependent,
    mediateSelectedListInfoDependent,
    mediateUpdateEntriesRequester,
} from 'mediators';
import { UpdateEntriesSignal, EVENT_KEYS } from 'presentation/events';
import { uploadStatement, confirmStatement } from 'core/services/statements.service';
import { getEntries } from 'core/services/tasks.service';
import {
    StatementRow,
    DraftEntry,
    MatchResult,
    parseAmount,
    parseDate,
    scoreMatch,
    buildMatches,
    buildBackendRow,
    toDateStr,
} from './statement.utils';
import { EntryFormDialog } from './EntryFormDialog';

// ─── CSV Parsing ──────────────────────────────────────────────────────────────

const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    for (const line of lines) {
        if (!line.trim()) continue;
        const cols: string[] = [];
        let inQuotes = false;
        let cur = '';
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
                else inQuotes = !inQuotes;
            } else if (ch === ',' && !inQuotes) {
                cols.push(cur.trim());
                cur = '';
            } else {
                cur += ch;
            }
        }
        cols.push(cur.trim());
        rows.push(cols);
    }
    return rows;
};

// ─── Bank Presets ─────────────────────────────────────────────────────────────

type BankPreset = {
    name: string;
    dateCol: number;
    descCol: number;
    amountCol: number | null;
    debitCol: number | null;
    creditCol: number | null;
    skipRows: number;
};

const BANK_PRESETS: Record<string, BankPreset> = {
    rbc: {
        name: 'RBC',
        dateCol: 2,
        descCol: 4,
        amountCol: 6,
        debitCol: null,
        creditCol: null,
        skipRows: 0,
    },
    bnc: {
        name: 'BNC',
        dateCol: 0,
        descCol: 1,
        amountCol: null,
        debitCol: 2,
        creditCol: 3,
        skipRows: 1,
    },
    desjardins: {
        name: 'Desjardins',
        dateCol: 0,
        descCol: 1,
        amountCol: null,
        debitCol: 2,
        creditCol: 3,
        skipRows: 1,
    },
    custom: {
        name: 'Custom',
        dateCol: 0,
        descCol: 1,
        amountCol: 2,
        debitCol: null,
        creditCol: null,
        skipRows: 1,
    },
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: { display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc' },

        // Pick phase
        pickWrap: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 },
        dropZone: {
            border: '2px dashed #94a3b8',
            borderRadius: 12,
            padding: '48px 64px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background 0.2s',
            '&:hover': { borderColor: '#1e3a8a', background: '#f0f4ff' },
        },
        dropIcon: { fontSize: 48, color: '#94a3b8', marginBottom: 12 },

        // Map phase
        mapScroll: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
        sectionTitle: { fontWeight: 700, fontSize: '0.85rem', margin: '16px 0 8px', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: 1 },
        presetChip: { marginRight: 8, marginBottom: 4 },
        colRow: { display: 'flex', alignItems: 'center', marginBottom: 8 },
        colLabel: { width: 180, fontSize: '0.82rem', color: '#555', flexShrink: 0 },
        colSelect: { minWidth: 220 },
        skipRow: { display: 'flex', alignItems: 'center', marginTop: 8 },
        skipBtn: { minWidth: 28, padding: '2px 6px' },
        skipNum: { width: 28, textAlign: 'center', fontSize: '0.9rem', fontWeight: 600 },
        previewCell: { fontSize: '0.72rem', padding: '4px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 },

        // Reconcile phase
        summaryBar: { display: 'flex', background: 'white', borderBottom: '1px solid #e2e8f0', padding: '10px 0' },
        summaryItem: { flex: 1, textAlign: 'center' },
        summaryNum: { fontSize: 22, fontWeight: 700, lineHeight: 1 },
        summaryLabel: { fontSize: 10, color: '#94a3b8', marginTop: 2 },
        matchList: { flex: 1, overflowY: 'auto', padding: '0 12px 80px' },
        matchCard: {
            margin: '8px 0',
            borderRadius: 8,
            padding: '10px 14px',
            background: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            borderLeft: '3px solid transparent',
        },
        matchCardConfirmed: { borderLeftColor: '#16a34a', background: '#f0fdf4' },
        matchCardCreate: { borderLeftColor: '#2563eb', background: '#eff6ff' },
        matchCardSkipped: { opacity: 0.45 },
        matchTop: { display: 'flex', alignItems: 'flex-start', gap: 8 },
        matchStmt: { flex: '0 0 160px' },
        matchArrow: { flex: '0 0 52px', textAlign: 'center', paddingTop: 4 },
        matchEntry: { flex: 1 },
        matchActions: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' },
        scoreText: { fontSize: 11, fontWeight: 700 },
        applyBar: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            padding: '12px 20px',
            background: 'white',
            borderTop: '1px solid #e2e8f0',
            boxShadow: '0 -2px 6px rgba(0,0,0,0.05)',
        },
    })
);

// ─── Column Select ────────────────────────────────────────────────────────────

const ColSelect = ({ label, value, headerRow, onChange }: any) => {
    const classes = useStyles();
    return (
        <Box className={classes.colRow}>
            <Typography className={classes.colLabel}>{label}</Typography>
            <FormControl variant="outlined" size="small" className={classes.colSelect}>
                <Select value={value} onChange={(e) => onChange(e.target.value as number)}>
                    <MenuItem value={-1}><em>— none —</em></MenuItem>
                    {headerRow.map((col: string, i: number) => (
                        <MenuItem key={i} value={i}>[{i}] {col || `Column ${i}`}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

// ─── Score color ──────────────────────────────────────────────────────────────

const scoreColor = (s: number) => s >= 0.7 ? '#16a34a' : s >= 0.4 ? '#d97706' : '#94a3b8';

// ─── Main Component ───────────────────────────────────────────────────────────

export const StatementImport = ({ onDone }: { onDone?: () => void } = {}) => {
    const classes = useStyles();
    const entries: any[] = useSelector(mediateEntriesInfoDependent) || [];
    const selectedList: any = useSelector(mediateSelectedListInfoDependent);
    const login: any = useSelector((state: any) => state.login);
    const dispatch = useDispatch();

    const [updateEntriesSignal] = useState(new UpdateEntriesSignal());
    useEffect(() => mediateUpdateEntriesRequester(updateEntriesSignal, dispatch).destroy, [updateEntriesSignal, dispatch]);

    const [phase, setPhase] = useState<'pick' | 'map' | 'reconcile'>('pick');
    const [rawRows, setRawRows] = useState<string[][]>([]);
    const [rawCsvText, setRawCsvText] = useState('');
    const [preset, setPreset] = useState<string>('custom');
    const [config, setConfig] = useState<BankPreset>(BANK_PRESETS.custom);
    const [matches, setMatches] = useState<MatchResult[]>([]);
    const [saving, setSaving] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [fileError, setFileError] = useState('');
    const [processError, setProcessError] = useState('');
    const [assigningIndex, setAssigningIndex] = useState<number | null>(null);
    const [applied, setApplied] = useState(false);
    const [applyResult, setApplyResult] = useState<{ stored: number; skipped: number } | null>(null);

    // Entry form dialog state
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingMode, setEditingMode] = useState<'confirm' | 'create'>('create');
    const [editForm, setEditForm] = useState<DraftEntry>({ title: '', amount: '', type: 'DEBIT', category: '', tag: '', date: '' });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const headerRow = rawRows[0] || [];

    // ── File pick ─────────────────────────────────────────────────────────────

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileError('');
        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target?.result as string;
            const rows = parseCSV(text);
            if (rows.length < 2) {
                setFileError('The CSV appears to have no data rows.');
                return;
            }
            setRawRows(rows);
            setRawCsvText(text);
            setPreset('custom');
            setConfig({ ...BANK_PRESETS.custom });
            setPhase('map');
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    // ── Preset change ─────────────────────────────────────────────────────────

    const handlePresetChange = (key: string) => {
        setPreset(key);
        setConfig({ ...BANK_PRESETS[key] });
    };

    // ── Build statements from raw rows ────────────────────────────────────────

    const buildStatements = (): StatementRow[] => {
        const dataRows = rawRows.slice(config.skipRows);
        return dataRows.map((row, i) => {
            const rawDate = row[config.dateCol] || '';
            const description = row[config.descCol] || '';
            let amount = 0;
            if (config.amountCol !== null && config.amountCol >= 0) {
                amount = parseAmount(row[config.amountCol] || '');
            } else {
                const debit = config.debitCol !== null && config.debitCol >= 0
                    ? parseAmount(row[config.debitCol] || '') : 0;
                const credit = config.creditCol !== null && config.creditCol >= 0
                    ? parseAmount(row[config.creditCol] || '') : 0;
                amount = credit - debit;
            }
            return { rawDate, date: parseDate(rawDate), description, amount, index: i };
        }).filter(s => s.amount !== 0 || s.description);
    };

    // ── Process ───────────────────────────────────────────────────────────────

    const BACKEND_BANKS: Record<string, 'rbc' | 'nbc'> = { rbc: 'rbc', bnc: 'nbc' };

    const handleProcess = async () => {
        setProcessing(true);
        setProcessError('');
        try {
            let stmtRows: StatementRow[];
            const backendBank = BACKEND_BANKS[preset];

            if (backendBank && rawCsvText && login?.user?.id) {
                const result = await uploadStatement(login.user.id, backendBank, rawCsvText);
                stmtRows = result.rows.map((r, i) => ({
                    rawDate: r.date,
                    date: new Date(r.date + 'T12:00:00'),
                    description: r.title,
                    amount: r.type === 'CREDIT' ? r.amount : -r.amount,
                    index: i,
                }));
            } else {
                stmtRows = buildStatements();
            }

            setMatches(buildMatches(stmtRows, entries));
            setPhase('reconcile');
            setApplied(false);
            setApplyResult(null);
        } catch (err) {
            setProcessError('Could not parse the statement. Check your bank selection or column mapping.');
        } finally {
            setProcessing(false);
        }
    };

    // ── Match actions ─────────────────────────────────────────────────────────

    const setMatch = (i: number, update: Partial<MatchResult>) =>
        setMatches(prev => prev.map((m, idx) => idx === i ? { ...m, ...update } : m));

    const handleSkip = (i: number) =>
        setMatch(i, { skipped: !matches[i].skipped, confirmed: false, createEntry: false });

    const handleAssignEntry = (entry: any) => {
        if (assigningIndex === null) return;
        const score = scoreMatch(matches[assigningIndex].statement, entry);
        setMatch(assigningIndex, { matchedEntry: entry, score, confirmed: false, skipped: false, createEntry: false });
        setAssigningIndex(null);
    };

    // ── Entry form dialog ─────────────────────────────────────────────────────

    const openConfirmForm = (idx: number) => {
        const match = matches[idx];
        const entry = match.matchedEntry;
        const draft = match.draftEntry;
        setEditForm({
            title: draft?.title || entry?.title || match.statement.description || '',
            amount: draft?.amount || String(entry?.amount || Math.abs(match.statement.amount)),
            type: draft?.type || entry?.type || (match.statement.amount > 0 ? 'CREDIT' : 'DEBIT'),
            category: draft?.category || entry?.category || '',
            tag: draft?.tag || entry?.tag || '',
            date: draft?.date || (entry?.date ? String(entry.date).slice(0, 10) : toDateStr(match.statement.date)),
        });
        setEditingMode('confirm');
        setEditingIndex(idx);
    };

    const openCreateForm = (idx: number) => {
        const match = matches[idx];
        const draft = match.draftEntry;
        setEditForm({
            title: draft?.title || match.statement.description || '',
            amount: draft?.amount || String(Math.abs(match.statement.amount)),
            type: draft?.type || (match.statement.amount > 0 ? 'CREDIT' : 'DEBIT'),
            category: draft ? draft.category : (match.matchedEntry?.category || ''),
            tag: draft ? draft.tag : (match.matchedEntry?.tag || ''),
            date: draft?.date || toDateStr(match.statement.date),
        });
        setEditingMode('create');
        setEditingIndex(idx);
    };

    const handleFormSave = (values: DraftEntry) => {
        if (editingIndex === null) return;
        if (editingMode === 'confirm') {
            setMatch(editingIndex, { confirmed: true, skipped: false, createEntry: false, draftEntry: { ...values } });
        } else {
            setMatch(editingIndex, { createEntry: true, skipped: false, confirmed: false, draftEntry: { ...values } });
        }
        setEditingIndex(null);
    };

    // ── Apply ─────────────────────────────────────────────────────────────────

    const confirmedCount = matches.filter(m => m.confirmed && m.matchedEntry && !m.skipped).length;
    const newCount = matches.filter(m => m.createEntry && !m.skipped).length;

    const handleApplyAll = async () => {
        if (!login?.user?.id || !selectedList?.id) return;
        setSaving(true);

        const userId = login.user.id;
        const listId = selectedList.id;

        const confirmedMatches = matches.filter(m => m.confirmed && m.matchedEntry && !m.skipped);
        const confirmedIds = new Set(confirmedMatches.map(m => m.matchedEntry.id));

        const createRows = matches
            .filter(m => m.createEntry && !m.skipped)
            .map(buildBackendRow);

        try {
            let confirmResult: { stored: number; skipped: number; listid: string } | null = null;
            if (createRows.length > 0) {
                confirmResult = await confirmStatement(userId, listId, createRows);
            }

            const freshData = await getEntries(userId, listId);
            const freshEntries: any[] = freshData?.entry?.entries || entries;

            if (confirmedIds.size > 0) {
                const merged = freshEntries.map((e: any) => {
                    if (confirmedIds.has(e.id)) {
                        const match = confirmedMatches.find(m => m.matchedEntry?.id === e.id);
                        const draft = match?.draftEntry;
                        return {
                            ...e,
                            ...(draft?.title ? { title: draft.title } : {}),
                            ...(draft?.category ? { category: draft.category } : {}),
                            ...(draft?.tag ? { tag: draft.tag } : {}),
                            ...(draft?.date ? { date: draft.date } : {}),
                            ...(draft?.onCredit != null ? { onCredit: draft.onCredit } : {}),
                        };
                    }
                    return e;
                });
                updateEntriesSignal.dispatch(merged);
            } else {
                dispatch({ type: EVENT_KEYS.SET_ENTRIES, payload: { entries: freshEntries } });
            }

            if (confirmResult) setApplyResult({ stored: confirmResult.stored, skipped: confirmResult.skipped });
            setApplied(true);
            setTimeout(() => onDone?.(), 1500);
        } catch (err) {
            // keep saving=false so user can retry
        } finally {
            setSaving(false);
        }
    };

    const availableEntries = entries.filter(e => !e.complete && !e.ignore && !e.parent);

    // ─── RENDER ───────────────────────────────────────────────────────────────

    // Phase: pick
    if (phase === 'pick') {
        return (
            <Box className={classes.root}>
                <Box className={classes.pickWrap}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Box className={classes.dropZone} onClick={() => fileInputRef.current?.click()}>
                        <CloudUpload className={classes.dropIcon} />
                        <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
                            Import Bank Statement
                        </Typography>
                        <Typography variant="body2" style={{ color: '#64748b', marginBottom: 20, maxWidth: 320 }}>
                            Export a CSV from your bank's website and import it here to reconcile against your budget entries.
                        </Typography>
                        <Button variant="contained" color="primary" size="large">
                            Choose CSV File
                        </Button>
                        <Typography variant="caption" style={{ display: 'block', color: '#94a3b8', marginTop: 12 }}>
                            Supports RBC, BNC, Desjardins and custom formats
                        </Typography>
                    </Box>
                    {fileError && (
                        <Typography variant="body2" style={{ color: '#dc2626', marginTop: 12 }}>
                            {fileError}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    // Phase: map
    if (phase === 'map') {
        const previewRows = rawRows.slice(0, Math.min(5, rawRows.length));
        return (
            <Box className={classes.root}>
                <Box className={classes.mapScroll}>
                    <Typography className={classes.sectionTitle}>1. Select your bank</Typography>
                    <Box mb={2}>
                        {Object.entries(BANK_PRESETS).map(([key, p]) => (
                            <Chip
                                key={key}
                                label={p.name}
                                onClick={() => handlePresetChange(key)}
                                color={preset === key ? 'primary' : 'default'}
                                className={classes.presetChip}
                                size="small"
                            />
                        ))}
                    </Box>

                    <Divider />

                    <Typography className={classes.sectionTitle}>2. Map columns</Typography>
                    <Typography variant="caption" style={{ color: '#64748b', display: 'block', marginBottom: 4 }}>
                        {rawRows.length - config.skipRows} data rows detected.
                        {(preset === 'rbc' || preset === 'bnc') && (
                            <span style={{ color: '#16a34a', marginLeft: 8 }}>
                                ✓ Parsed server-side — column mapping not required
                            </span>
                        )}
                    </Typography>

                    <ColSelect label="Date column" value={config.dateCol} headerRow={headerRow}
                        onChange={(v: number) => setConfig(c => ({ ...c, dateCol: v }))} />
                    <ColSelect label="Description column" value={config.descCol} headerRow={headerRow}
                        onChange={(v: number) => setConfig(c => ({ ...c, descCol: v }))} />

                    <Box className={classes.colRow} style={{ marginTop: 8, marginBottom: 8 }}>
                        <Typography className={classes.colLabel}>Amount format</Typography>
                        <Button
                            size="small"
                            variant={config.amountCol !== null ? 'contained' : 'outlined'}
                            color={config.amountCol !== null ? 'primary' : 'default'}
                            onClick={() => setConfig(c => ({ ...c, amountCol: 2, debitCol: null, creditCol: null }))}
                            style={{ marginRight: 8 }}
                        >
                            Single column
                        </Button>
                        <Button
                            size="small"
                            variant={config.amountCol === null ? 'contained' : 'outlined'}
                            color={config.amountCol === null ? 'primary' : 'default'}
                            onClick={() => setConfig(c => ({ ...c, amountCol: null, debitCol: 2, creditCol: 3 }))}
                        >
                            Debit / Credit
                        </Button>
                    </Box>

                    {config.amountCol !== null ? (
                        <ColSelect label="Amount column (neg = debit)" value={config.amountCol} headerRow={headerRow}
                            onChange={(v: number) => setConfig(c => ({ ...c, amountCol: v }))} />
                    ) : (
                        <>
                            <ColSelect label="Debit column (money out)" value={config.debitCol ?? -1} headerRow={headerRow}
                                onChange={(v: number) => setConfig(c => ({ ...c, debitCol: v }))} />
                            <ColSelect label="Credit column (money in)" value={config.creditCol ?? -1} headerRow={headerRow}
                                onChange={(v: number) => setConfig(c => ({ ...c, creditCol: v }))} />
                        </>
                    )}

                    <Box className={classes.skipRow}>
                        <Typography className={classes.colLabel}>Skip header rows</Typography>
                        <Button size="small" variant="outlined" className={classes.skipBtn}
                            onClick={() => setConfig(c => ({ ...c, skipRows: Math.max(0, c.skipRows - 1) }))}>
                            −
                        </Button>
                        <Typography className={classes.skipNum}>{config.skipRows}</Typography>
                        <Button size="small" variant="outlined" className={classes.skipBtn}
                            onClick={() => setConfig(c => ({ ...c, skipRows: c.skipRows + 1 }))}>
                            +
                        </Button>
                    </Box>

                    <Divider style={{ margin: '16px 0 8px' }} />

                    <Typography className={classes.sectionTitle}>3. Preview</Typography>
                    <TableContainer component={Paper} variant="outlined" style={{ overflowX: 'auto', marginBottom: 16 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow style={{ background: '#f1f5f9' }}>
                                    {headerRow.map((col, i) => (
                                        <TableCell key={i} className={classes.previewCell} style={{ fontWeight: 700 }}>
                                            [{i}] {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewRows.slice(1).map((row, ri) => (
                                    <TableRow key={ri}>
                                        {row.map((cell, ci) => (
                                            <TableCell key={ci} className={classes.previewCell}>{cell}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box className={classes.applyBar}>
                    {processError && (
                        <Typography variant="caption" style={{ color: '#dc2626', flex: 1 }}>
                            {processError}
                        </Typography>
                    )}
                    <Button onClick={() => setPhase('pick')}>Back</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={processing}
                        onClick={handleProcess}
                        startIcon={processing ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        {processing ? 'Parsing…' : 'Find Matches'}
                    </Button>
                </Box>
            </Box>
        );
    }

    // Phase: reconcile
    const unmatchedCount = matches.filter(m => !m.matchedEntry && !m.skipped && !m.createEntry).length;
    const skippedCount = matches.filter(m => m.skipped).length;

    return (
        <Box className={classes.root}>
            {applied ? (
                <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleOutline style={{ fontSize: 56, color: '#16a34a', marginBottom: 12 }} />
                    <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 6 }}>Done!</Typography>
                    <Typography variant="body2" style={{ color: '#64748b', marginBottom: 24 }}>
                        {confirmedCount > 0 && `${confirmedCount} ${confirmedCount === 1 ? 'entry' : 'entries'} marked complete. `}
                        {applyResult && `${applyResult.stored} new ${applyResult.stored === 1 ? 'entry' : 'entries'} imported${applyResult.skipped > 0 ? `, ${applyResult.skipped} duplicate${applyResult.skipped > 1 ? 's' : ''} skipped` : ''}.`}
                        {!confirmedCount && !applyResult && 'All done.'}
                    </Typography>
                    <Button variant="outlined" onClick={() => { setPhase('pick'); setRawRows([]); setMatches([]); setApplied(false); }}>
                        Import another file
                    </Button>
                </Box>
            ) : (
                <>
                    {/* Summary bar */}
                    <Box className={classes.summaryBar}>
                        {[
                            { num: matches.length, label: 'Rows', color: undefined },
                            { num: confirmedCount, label: 'Reconciled', color: '#16a34a' },
                            { num: newCount, label: 'To create', color: '#2563eb' },
                            { num: unmatchedCount, label: 'Unmatched', color: unmatchedCount > 0 ? '#d97706' : '#94a3b8' },
                            { num: skippedCount, label: 'Skipped', color: '#94a3b8' },
                        ].map(({ num, label, color }) => (
                            <Box key={label} className={classes.summaryItem}>
                                <Typography className={classes.summaryNum} style={color ? { color } : {}}>
                                    {num}
                                </Typography>
                                <Typography className={classes.summaryLabel}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Match rows */}
                    <Box className={classes.matchList}>
                        {matches.map((match, idx) => {
                            const { statement: stmt, matchedEntry, score, confirmed, skipped, createEntry, draftEntry } = match;
                            const amtColor = stmt.amount > 0 ? '#16a34a' : '#dc2626';
                            const amtStr = `${stmt.amount > 0 ? '+' : ''}$${Math.abs(stmt.amount).toFixed(2)}`;
                            const cardClass = [
                                classes.matchCard,
                                confirmed ? classes.matchCardConfirmed : '',
                                createEntry ? classes.matchCardCreate : '',
                                skipped ? classes.matchCardSkipped : '',
                            ].filter(Boolean).join(' ');

                            return (
                                <Paper key={idx} className={cardClass} elevation={0}>
                                    <Box className={classes.matchTop}>
                                        {/* Statement */}
                                        <Box className={classes.matchStmt}>
                                            <Typography variant="caption" style={{ color: '#94a3b8', display: 'block' }}>
                                                {stmt.rawDate}
                                            </Typography>
                                            <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#1e293b', marginBottom: 2 }}>
                                                {stmt.description}
                                            </Typography>
                                            <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: amtColor }}>
                                                {amtStr}
                                            </Typography>
                                        </Box>

                                        {/* Arrow + score */}
                                        <Box className={classes.matchArrow}>
                                            <Typography className={classes.scoreText} style={{ color: scoreColor(score) }}>
                                                {matchedEntry ? `${Math.round(score * 100)}%` : '—'}
                                            </Typography>
                                            <Typography style={{ fontSize: 18, color: '#cbd5e1' }}>→</Typography>
                                        </Box>

                                        {/* Entry side */}
                                        <Box className={classes.matchEntry}>
                                            {confirmed && draftEntry ? (
                                                <>
                                                    <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#15803d', marginBottom: 2, fontWeight: 600 }}>
                                                        {draftEntry.title}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#64748b' }}>
                                                        ${Number(draftEntry.amount).toFixed(2)} · {draftEntry.type}
                                                        {draftEntry.tag ? ` · ${draftEntry.tag}` : ''}
                                                    </Typography>
                                                </>
                                            ) : createEntry && draftEntry ? (
                                                <>
                                                    <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#1d4ed8', marginBottom: 2, fontStyle: 'italic' }}>
                                                        {draftEntry.title}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#64748b' }}>
                                                        ${Number(draftEntry.amount).toFixed(2)} · {draftEntry.type}
                                                        {draftEntry.tag ? ` · ${draftEntry.tag}` : ''}
                                                    </Typography>
                                                </>
                                            ) : matchedEntry ? (
                                                <>
                                                    <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#1e293b', marginBottom: 2 }}>
                                                        {matchedEntry.title}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: '#64748b' }}>
                                                        ${Number(matchedEntry.amount).toFixed(2)} · {matchedEntry.type}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                    No match found
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Actions */}
                                    <Box className={classes.matchActions}>
                                        {/* Confirm — only when matched, not yet confirmed/skipped/creating */}
                                        {!skipped && !confirmed && !createEntry && matchedEntry && (
                                            <Button size="small" variant="contained" color="primary"
                                                onClick={() => openConfirmForm(idx)} style={{ minWidth: 80 }}>
                                                Confirm
                                            </Button>
                                        )}

                                        {/* Confirmed chip — click to re-edit */}
                                        {!skipped && confirmed && (
                                            <Chip
                                                size="small"
                                                icon={<CheckCircleOutline style={{ fontSize: 14 }} />}
                                                label="Confirmed"
                                                style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: 11 }}
                                                onClick={() => openConfirmForm(idx)}
                                                deleteIcon={<Edit style={{ fontSize: 12 }} />}
                                                onDelete={() => openConfirmForm(idx)}
                                            />
                                        )}

                                        {/* Change — reassign matched entry (not when confirmed or creating) */}
                                        {!skipped && matchedEntry && !confirmed && !createEntry && (
                                            <Button size="small" variant="outlined"
                                                onClick={() => setAssigningIndex(idx)}>
                                                Change
                                            </Button>
                                        )}

                                        {/* Create — available when not skipped, not confirmed, not already creating */}
                                        {!skipped && !confirmed && !createEntry && (
                                            <Button size="small" variant="outlined" color="primary"
                                                startIcon={<AddCircleOutline style={{ fontSize: 14 }} />}
                                                onClick={() => openCreateForm(idx)}>
                                                Create
                                            </Button>
                                        )}

                                        {/* Will create chip — click to re-edit */}
                                        {!skipped && createEntry && (
                                            <Chip
                                                size="small"
                                                icon={<AddCircleOutline style={{ fontSize: 14 }} />}
                                                label="Will create"
                                                style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: 11 }}
                                                onClick={() => openCreateForm(idx)}
                                                deleteIcon={<Edit style={{ fontSize: 12 }} />}
                                                onDelete={() => openCreateForm(idx)}
                                            />
                                        )}

                                        {/* Assign — only when no match, not creating/confirmed */}
                                        {!skipped && !matchedEntry && !createEntry && !confirmed && (
                                            <Button size="small" onClick={() => setAssigningIndex(idx)}
                                                style={{ color: '#64748b' }}>
                                                Assign
                                            </Button>
                                        )}

                                        {/* Skip / Undo */}
                                        <Button size="small" onClick={() => handleSkip(idx)}
                                            style={{ color: '#94a3b8', marginLeft: 'auto' }}>
                                            {skipped ? 'Undo' : 'Skip'}
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>

                    {/* Apply bar */}
                    <Box className={classes.applyBar}>
                        <Button onClick={() => setPhase('map')}>← Back</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={(confirmedCount === 0 && newCount === 0) || saving}
                            onClick={handleApplyAll}
                            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            {saving ? 'Saving…' : `Apply${confirmedCount + newCount > 0 ? ` (${confirmedCount + newCount})` : ''}`}
                        </Button>
                    </Box>
                </>
            )}

            {/* Entry form dialog (Confirm or Create) */}
            <EntryFormDialog
                open={editingIndex !== null}
                dialogTitle={editingMode === 'confirm' ? 'Confirm Entry' : 'Create New Entry'}
                saveLabel={editingMode === 'confirm' ? 'Confirm' : 'Create'}
                initialValues={editForm}
                onSave={values => handleFormSave(values as DraftEntry)}
                onClose={() => setEditingIndex(null)}
            />

            {/* Assign dialog */}
            <Dialog open={assigningIndex !== null} onClose={() => setAssigningIndex(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Choose entry to match</DialogTitle>
                <DialogContent dividers style={{ padding: 0, maxHeight: 400 }}>
                    <List disablePadding>
                        {availableEntries.map(entry => (
                            <ListItem
                                key={entry.id}
                                button
                                divider
                                onClick={() => handleAssignEntry(entry)}
                            >
                                <ListItemText
                                    primary={entry.title}
                                    secondary={entry.date
                                        ? new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                        : undefined}
                                    primaryTypographyProps={{ style: { fontSize: '0.9rem' } }}
                                    secondaryTypographyProps={{ style: { fontSize: '0.75rem' } }}
                                />
                                <ListItemSecondaryAction>
                                    <Typography style={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: entry.type === 'CREDIT' ? '#16a34a' : '#dc2626',
                                    }}>
                                        ${Number(entry.amount).toFixed(2)}
                                    </Typography>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {availableEntries.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="No available entries"
                                    secondary="All entries are already complete or matched."
                                    primaryTypographyProps={{ style: { color: '#94a3b8' } }}
                                />
                            </ListItem>
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssigningIndex(null)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
